import { watch } from 'vue';
import { tryOnUnmounted, useScrollLock } from '@vueuse/core';

import { UseToggle, useToggle } from './useToggle';
import { RouteLocationNamedRaw, useRoute, useRouter } from 'vue-router';
import { computed } from 'vue';

export type UseModal = {
  open: UseToggle['value'];
  toggleOpen: UseToggle['toggle'];
  setOpen: UseToggle['setOn'];
  setClosed: UseToggle['setOff'];
};

type ModalRouting = {
  /*
  The route to open the modal
  */
  openRoutes: RouteLocationNamedRaw[];
  /*
  The route to close the modal
  */
  closeRoute: RouteLocationNamedRaw;
  /*
  A list of hashes that can prevent the route from being modified
  */
  closeRouteOverrideHashes?: string[];
};

const modals: UseModal[] = [];

export const useModal = (modalRouting?: ModalRouting): UseModal => {
  const { value: open, toggle: toggleOpen, setOn: setOpen, setOff: setClosed } = useToggle();

  const modal: UseModal = {
    open,
    toggleOpen,
    setOpen,
    setClosed,
  };

  modals.push(modal);

  const scrollLock = useScrollLock(document.body);

  watch(open, () => {
    const isAnyOpen = Boolean(modals.find((modal) => modal.open.value));

    if (isAnyOpen) {
      scrollLock.value = true;
    } else {
      scrollLock.value = false;
    }
  });

  setupModalRouting(modal, modalRouting);

  tryOnUnmounted(() => {
    setClosed();
  });

  return modal;
};

function setupModalRouting(modal: UseModal, modalRouting?: ModalRouting) {
  if (!modalRouting) {
    return;
  }

  const route = useRoute();
  const router = useRouter();

  const isOnOpenRoute = computed(() => modalRouting.openRoutes.some((openRoute) => route.name === openRoute.name));
  const noHashRequired = computed(() => modalRouting.openRoutes.every((openRoute) => openRoute.hash == null));

  watch(
    () => route.fullPath,
    () => {
      const hashMatches = modalRouting.openRoutes.some((openRoute) => route.hash === openRoute.hash);

      const isModalOpenRoute = isOnOpenRoute.value && (noHashRequired.value || hashMatches);

      if (isModalOpenRoute) {
        modal.setOpen();
      } else {
        modal.setClosed();
      }
    },
    { immediate: true }
  );

  watch(
    () => modal.open.value,
    (isOpen) => {
      if (isOpen) {
        const isAlreadyOnOpenRoute = modalRouting.openRoutes.some((openRoute) => route.name === openRoute.name);
        if (!isAlreadyOnOpenRoute) {
          router.replace(modalRouting.openRoutes[0]);
        }
      } else {
        const isOnOpenRoute = modalRouting.openRoutes.some((openRoute) => route.name === openRoute.name);

        const noHashRequired = modalRouting.openRoutes.every((openRoute) => openRoute.hash == null);
        const isAllowedHash = modalRouting.closeRouteOverrideHashes?.includes(route.hash) ?? false;
        const isDifferentFromOpenHash = !modalRouting.openRoutes.some(
          (openRoute) => openRoute.name === route.name && openRoute.hash === route.hash
        );

        const isAllowedToCloseFromCurrentHash = !noHashRequired && isAllowedHash && isDifferentFromOpenHash;

        // Prevent routing if closing from the open route with an allowed hash.
        // This handles the edge case where the URL hash is manually changed
        // to another modal's identifier, preventing an unwanted route replacement.
        if (isOnOpenRoute && isAllowedToCloseFromCurrentHash) {
          return;
        }

        router.replace(modalRouting.closeRoute);
      }
    }
  );
}
